// import { queryOpenRouter, JURISDICTION_MODEL } from '@/lib/openrouter';
import { interpolatePrompt, parseJsonResponse } from './ai-utils';
import { queryBlocksAgent } from '@/lib/blocks-ai-agent';

export interface DynamicField {
  id: string;
  name: string;
  label: string;
  type: string;
  required: boolean;
  placeholder?: string;
  helpText?: string;
  options: string[];
  standardValue?: string;
}

export interface GenerateDynamicFieldsResult {
  fields: DynamicField[];
  reasoning: string | null;
  jurisdictionName: string | null;
}

const SUPPORTED_FIELD_TYPES = ['text', 'email', 'date', 'number', 'checkbox', 'select'] as const;

const FIELD_TYPE_DESCRIPTIONS: Record<
  string,
  { description: string; properties: string[]; example: string }
> = {
  text: {
    description: 'Single-line text input for short answers',
    properties: ['placeholder', 'helpText'],
    example: "Company name, person's name, short description",
  },
  email: {
    description: 'Email address input with validation',
    properties: ['placeholder', 'helpText'],
    example: 'Contact email, notification email',
  },
  date: {
    description: 'Date picker for selecting dates',
    properties: ['placeholder', 'helpText'],
    example: 'Agreement date, expiration date, effective date',
  },
  number: {
    description: 'Numeric input for quantities or amounts',
    properties: ['placeholder', 'helpText'],
    example: 'Duration in months, monetary amounts, quantities',
  },
  checkbox: {
    description: 'Yes/No toggle for boolean options',
    properties: ['helpText'],
    example: 'Agree to terms, include optional clause, enable feature',
  },
  select: {
    description: 'Dropdown menu for choosing from predefined options',
    properties: ['options', 'helpText'],
    example: 'Jurisdiction selection, contract type, duration options',
  },
};

function generateFieldSchemaDocumentation(): string {
  return SUPPORTED_FIELD_TYPES.map((type) => {
    const info = FIELD_TYPE_DESCRIPTIONS[type] || {
      description: `${type} field`,
      properties: ['helpText'],
      example: 'General input',
    };
    return `- **${type}**: ${info.description}
    - Properties: ${info.properties.join(', ')}
    - Use for: ${info.example}`;
  }).join('\n');
}

function buildDynamicFieldsSystemPrompt(maxFields: number): string {
  const fieldSchemaDoc = generateFieldSchemaDocumentation();
  const fieldTypesList = SUPPORTED_FIELD_TYPES.join(', ');

  return `You are an AI assistant helping to create dynamic form fields for a legal document generator.

Your task is to generate relevant form questions based on the user's context and requirements.

## AVAILABLE FIELD TYPES

${fieldSchemaDoc}

## RULES

1. Generate between 1 and ${maxFields} fields maximum
2. Each field must have a unique "name" (camelCase, no spaces or special characters)
3. ONLY use these field types: ${fieldTypesList}
4. For "select" type fields, you MUST include an "options" array with string values
5. Make fields contextually relevant to the legal document being created
6. Include helpful "helpText" that explains why this information is needed (legal context)
7. **ALL fields should be OPTIONAL (required: false)** - users can skip this screen entirely
8. Generate fields that would gather information not already provided in the context
9. Choose the most appropriate field type for each question
10. **IMPORTANT**: For each field, provide a "standardValue" - this is the jurisdiction-specific standard/default answer that would typically be used in professional agreements for the detected jurisdiction
11. **CRITICAL**: Keep all text values concise. helpText should be 1-2 short sentences max. standardValue should be brief. Do NOT write verbose explanations. This prevents response truncation.
12. **CRITICAL**: Return ONLY the JSON object, no extra text before or after. The response must be valid parseable JSON.

## OUTPUT FORMAT

Return ONLY this JSON object (no markdown, no explanation outside the JSON):
{
  "fields": [
    {
      "name": "fieldNameInCamelCase",
      "label": "Human Readable Label",
      "type": "${fieldTypesList}",
      "required": false,
      "placeholder": "Optional placeholder text (not for checkbox)",
      "helpText": "Brief legal context (1-2 sentences max)",
      "options": ["only", "for", "select", "type"],
      "standardValue": "Brief standard/default value"
    }
  ],
  "reasoning": "Brief explanation (1-2 sentences)",
  "jurisdictionName": "Swiss"
}`;
}

function buildDynamicFieldsUserMessage(
  interpolatedPrompt: string,
  allContext: Record<string, unknown>,
  maxFields: number,
  screenTitle?: string,
  screenDescription?: string
): string {
  return `Context from previous form steps:
${JSON.stringify(allContext, null, 2)}

${screenTitle ? `Screen Title: ${screenTitle}` : ''}
${screenDescription ? `Screen Description: ${screenDescription}` : ''}

User's Request:
${interpolatedPrompt}

Generate appropriate form fields based on this context. Remember:
- Maximum ${maxFields} fields
- Only use supported field types: ${SUPPORTED_FIELD_TYPES.join(', ')}
- Make fields relevant to the legal document context`;
}

function validateAndSanitizeFields(fields: unknown[], maxFields: number): DynamicField[] {
  const validated: DynamicField[] = [];
  const usedNames = new Set<string>();

  for (const field of fields.slice(0, maxFields)) {
    if (!field || typeof field !== 'object') continue;
    const f = field as Record<string, unknown>;

    if (typeof f.name !== 'string' || !f.name.trim()) continue;
    if (typeof f.label !== 'string' || !f.label.trim()) continue;

    let name = f.name.replace(/[^a-zA-Z0-9]/g, '');
    if (!name || !/^[a-zA-Z]/.test(name)) name = 'field' + name;
    if (usedNames.has(name)) name = name + '_' + (usedNames.size + 1);
    usedNames.add(name);

    const type = (SUPPORTED_FIELD_TYPES as readonly string[]).includes(f.type as string)
      ? (f.type as string)
      : 'text';

    let options: string[] = [];
    if (type === 'select' && Array.isArray(f.options)) {
      options = f.options.filter((o): o is string => typeof o === 'string' && o.trim() !== '');
      if (options.length === 0) continue;
    }

    validated.push({
      id: `dynamic_${name}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      name,
      label: String(f.label).trim(),
      type,
      required: false,
      placeholder: typeof f.placeholder === 'string' ? f.placeholder : undefined,
      helpText: typeof f.helpText === 'string' ? f.helpText : undefined,
      options,
      standardValue: typeof f.standardValue === 'string' ? f.standardValue : undefined,
    });
  }

  return validated;
}

export async function generateDynamicFields(
  prompt: string,
  formData: Record<string, unknown>,
  enrichmentContext: Record<string, unknown>,
  options: {
    maxFields?: number;
    screenTitle?: string;
    screenDescription?: string;
  } = {},
  signal?: AbortSignal
): Promise<GenerateDynamicFieldsResult> {
  const { maxFields = 5, screenTitle, screenDescription } = options;
  const allContext = { ...formData, ...enrichmentContext };
  const interpolated = interpolatePrompt(prompt, allContext);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const systemPrompt = buildDynamicFieldsSystemPrompt(maxFields);
  const userMessage = buildDynamicFieldsUserMessage(
    interpolated,
    allContext,
    maxFields,
    screenTitle,
    screenDescription
  );

  // const response = await queryOpenRouter({
  //   messages: [
  //     { role: 'system', content: systemPrompt },
  //     { role: 'user', content: userMessage },
  //   ],
  //   model: JURISDICTION_MODEL,
  //   responseFormat: 'json',
  //   temperature: 0.7,
  //   signal,
  // });
  const agentId = import.meta.env.VITE_GENERATE_DYNAMIC_FIELDS_AI_AGENT_ID;
  const response = await queryBlocksAgent(agentId, userMessage, signal);

  if (!response?.message) {
    return { fields: [], reasoning: null, jurisdictionName: null };
  }

  let parsed: Record<string, unknown>;
  try {
    parsed = parseJsonResponse(response.message);
  } catch {
    return { fields: [], reasoning: null, jurisdictionName: null };
  }

  const rawFields = Array.isArray(parsed.fields) ? parsed.fields : [];
  const fields = validateAndSanitizeFields(rawFields, maxFields);

  return {
    fields,
    reasoning: typeof parsed.reasoning === 'string' ? parsed.reasoning : null,
    jurisdictionName: typeof parsed.jurisdictionName === 'string' ? parsed.jurisdictionName : null,
  };
}
